import { Control } from 'react-hook-form';
import { useIntl } from 'react-intl';
import { ContentGroup } from '@rossum/components/content-group/ContentGroup';
import { MenuItem } from '@rossum/ui/material';
import TextFieldControl from '../../../components/ReactHookForm/controls/TextFieldControl';
import { CredentialFormValues, credentialTypes } from '../core';
import { PermissionScopesControl } from './PermissionScopesControl';

type CredentialFormProps = {
  control: Control<CredentialFormValues>;
  isEditing?: boolean;
  disabled?: boolean;
};

export const CredentialForm = ({
  control,
  isEditing,
  disabled,
}: CredentialFormProps) => {
  const intl = useIntl();

  return (
    <>
      <ContentGroup
        title={intl.formatMessage({
          id: 'features.credentials.form.general.title',
        })}
        description={intl.formatMessage({
          id: 'features.credentials.form.general.description',
        })}
      >
        <TextFieldControl
          autoFocus
          disabled={disabled}
          ControllerProps={{ control, name: 'name' }}
          label={intl.formatMessage({
            id: 'features.credentials.form.general.name.label',
          })}
          FieldLabelProps={{ layout: 'floating' }}
          size="small"
        />
        <TextFieldControl
          select
          // The integration type cannot be changed once the credential exists.
          disabled={disabled || isEditing}
          ControllerProps={{ control, name: 'type' }}
          label={intl.formatMessage({
            id: 'features.credentials.form.general.type.label',
          })}
          FieldLabelProps={{ layout: 'floating' }}
          size="small"
        >
          {credentialTypes.map(credentialType => (
            <MenuItem key={credentialType} value={credentialType}>
              {intl.formatMessage({
                id: `features.credentials.type.${credentialType}`,
              })}
            </MenuItem>
          ))}
        </TextFieldControl>
        <TextFieldControl
          disabled={disabled}
          ControllerProps={{ control, name: 'baseApiUrl' }}
          label={intl.formatMessage({
            id: 'features.credentials.form.general.baseApiUrl.label',
          })}
          FieldLabelProps={{ layout: 'floating' }}
          size="small"
        />
      </ContentGroup>

      <ContentGroup
        title={intl.formatMessage({
          id: 'features.credentials.form.secret.title',
        })}
        description={intl.formatMessage({
          id: 'features.credentials.form.secret.description',
        })}
      >
        <TextFieldControl
          disabled={disabled}
          ControllerProps={{ control, name: 'clientId' }}
          label={intl.formatMessage({
            id: 'features.credentials.form.secret.clientId.label',
          })}
          FieldLabelProps={{ layout: 'floating' }}
          size="small"
        />
        <TextFieldControl
          disabled={disabled}
          ControllerProps={{ control, name: 'clientSecret' }}
          type="password"
          label={intl.formatMessage({
            id: 'features.credentials.form.secret.clientSecret.label',
          })}
          helperText={intl.formatMessage({
            id: 'features.credentials.form.secret.clientSecret.helperText',
          })}
          FieldLabelProps={{ layout: 'floating' }}
          size="small"
        />
        <PermissionScopesControl
          control={control}
          name="permissionScopes"
          disabled={disabled}
          label={intl.formatMessage({
            id: 'features.credentials.form.secret.permissionScopes.label',
          })}
          helperText={intl.formatMessage({
            id: 'features.credentials.form.secret.permissionScopes.helperText',
          })}
        />
      </ContentGroup>
    </>
  );
};
